import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient } from '../api/client';
import { EXCHANGES } from '../constants/exchanges';
import './AdminSettings.css';

function AdminSettings() {
  const { selectedAccountId } = useAuth();
  const { apiGet, apiFetch } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [keyStatus, setKeyStatus] = useState({});
  const [keyStatusSource, setKeyStatusSource] = useState(null); // 'account' | 'config' from API
  const [testingExchange, setTestingExchange] = useState(null);
  const [testResults, setTestResults] = useState({}); // { exchange: { success, message } }
  
  // Position opening times state
  const [openingTimes, setOpeningTimes] = useState([]);
  const [editingPosition, setEditingPosition] = useState(null);
  const [editDateTime, setEditDateTime] = useState('');
  const [editNote, setEditNote] = useState('');
  const [recalculatingPosition, setRecalculatingPosition] = useState(null);

  useEffect(() => {
    loadKeyStatus();
    loadOpeningTimes();
  }, [selectedAccountId]);

  const loadKeyStatus = async () => {
    try {
      const data = await apiGet('/api/admin/api-keys/status');
      setKeyStatus(data.status || {});
      setKeyStatusSource(data.source || null);
    } catch (err) {
      const errorMsg = err?.message || err?.toString() || '';
      if (errorMsg.toLowerCase().includes('user rejected') ||
          errorMsg.toLowerCase().includes('wallet') ||
          errorMsg.toLowerCase().includes('extension')) {
        console.debug('Suppressed wallet extension error in loadKeyStatus:', err);
        return;
      }
      console.error('Failed to load key status:', err);
    }
  };

  const loadOpeningTimes = async () => {
    try {
      const data = await apiGet('/api/admin/positions/opening-times');
      setOpeningTimes(data.positions || []);
    } catch (err) {
      const errorMsg = err?.message || err?.toString() || '';
      if (errorMsg.toLowerCase().includes('user rejected') ||
          errorMsg.toLowerCase().includes('wallet') ||
          errorMsg.toLowerCase().includes('extension')) {
        console.debug('Suppressed wallet extension error in loadOpeningTimes:', err);
        return;
      }
      console.error('Failed to load opening times:', err);
    }
  };

  const handleEditOpeningTime = (position) => {
    setEditingPosition(position);
    // Parse as UTC (API returns UTC with Z), then show local time in datetime-local so GMT+08 is preserved on save
    const dt = parseOpeningTimeAsUtc(position.opening_time);
    if (!dt || isNaN(dt.getTime())) {
      setEditDateTime('');
      setEditNote('');
      return;
    }
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    const h = String(dt.getHours()).padStart(2, '0');
    const min = String(dt.getMinutes()).padStart(2, '0');
    setEditDateTime(`${y}-${m}-${d}T${h}:${min}`);
    setEditNote('');
  };

  const handleSaveOpeningTime = async () => {
    if (!editingPosition) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Convert local datetime to ISO string
      const isoDateTime = new Date(editDateTime).toISOString();
      
      const response = await apiFetch('/api/admin/positions/opening-time', {
        method: 'POST',
        body: JSON.stringify({
          exchange: editingPosition.exchange,
          pair: editingPosition.pair,
          opening_time: isoDateTime,
          note: editNote,
        }),
      });

      if (response.ok) {
        setSuccess(`Opening time updated for ${editingPosition.exchange} ${editingPosition.pair}`);
        setEditingPosition(null);
        setEditDateTime('');
        setEditNote('');
        loadOpeningTimes();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(formatErrorDetail(errorData.detail) || 'Failed to update opening time');
      }
    } catch (err) {
      setError('Failed to update opening time. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateFunding = async (exchange, pair) => {
    setRecalculatingPosition(`${exchange}-${pair}`);
    setError('');
    setSuccess('');
    
    
    
    try {
      const requestBody = {
        exchange: exchange,
        pair: pair
      };
      
      
      
      const response = await apiFetch('/api/admin/positions/recalculate-funding', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Funding recalculated for ${exchange.toUpperCase()} ${pair}. ${data.message || ''}`);
      } else {
        const errorText = await response.text();
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { detail: errorText };
        }
        const errorMessage = errorData.detail
          ? formatErrorDetail(errorData.detail)
          : `Failed to recalculate funding for ${exchange} ${pair}`;
        setError(errorMessage);
      }
    } catch (err) {
      
      setError(`Failed to recalculate funding for ${exchange} ${pair}. Please try again.`);
    } finally {
      setRecalculatingPosition(null);
    }
  };

  // Treat API datetime as UTC when no offset (backend returns naive UTC with Z). Display in local time so GMT+08 is correct.
  const parseOpeningTimeAsUtc = (isoString) => {
    if (!isoString) return null;
    const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(isoString);
    return new Date(hasOffset ? isoString : isoString.replace(/\.\d{3}$/, '') + 'Z');
  };

  const formatDateTime = (isoString) => {
    const dt = parseOpeningTimeAsUtc(isoString);
    if (!dt || isNaN(dt.getTime())) return isoString || '';
    // Format in local time (so GMT+08 shows correct hour)
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    const hours = String(dt.getHours()).padStart(2, '0');
    const minutes = String(dt.getMinutes()).padStart(2, '0');
    const seconds = String(dt.getSeconds()).padStart(2, '0');
    const tzOffset = -dt.getTimezoneOffset();
    const tzHours = Math.floor(Math.abs(tzOffset) / 60);
    const tzMins = Math.abs(tzOffset) % 60;
    const tzSign = tzOffset >= 0 ? '+' : '-';
    const tzString = tzMins > 0 ? `${tzSign}${String(tzHours).padStart(2, '0')}:${String(tzMins).padStart(2, '0')}` : `${tzSign}${String(tzHours).padStart(2, '0')}`;
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds} GMT${tzString}`;
  };

  const formatErrorDetail = (detail) => {
    if (typeof detail === 'string') {
      return detail;
    }
    if (Array.isArray(detail)) {
      // FastAPI validation errors are arrays
      return detail.map(err => {
        if (typeof err === 'string') return err;
        if (err.msg) return `${err.loc?.join('.') || 'error'}: ${err.msg}`;
        return JSON.stringify(err);
      }).join('; ');
    }
    if (detail && typeof detail === 'object') {
      // Handle object errors
      if (detail.msg) return detail.msg;
      if (detail.message) return detail.message;
      return JSON.stringify(detail);
    }
    return String(detail || 'Unknown error');
  };

  const handleTest = async (exchange) => {
    setTestingExchange(exchange);

    try {
      const response = await apiFetch(`/api/admin/api-keys/test?exchange=${exchange}`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        setTestResults((prev) => ({ ...prev, [exchange]: { success: true, message: data.message } }));
      } else {
        setTestResults((prev) => ({ ...prev, [exchange]: { success: false, message: data.error } }));
      }
    } catch (err) {
      const errorMsg = err?.message || err?.toString() || '';
      if (errorMsg.toLowerCase().includes('user rejected') || 
          errorMsg.toLowerCase().includes('wallet') ||
          errorMsg.toLowerCase().includes('extension')) {
        console.debug('Suppressed wallet extension error in handleTest:', err);
        return;
      }
      setTestResults(prev => ({ ...prev, [exchange]: { success: false, message: errorMsg } }));
    } finally {
      setTestingExchange(null);
    }
  };

  // Exchange is "configured" when required credentials exist (from status API)
  const isExchangeConfigured = (exchange) => {
    const status = keyStatus[exchange];
    if (!status) return false;
    const required = {
      binance: ['api_key', 'secret_key'],
      bybit: ['api_key', 'secret_key'],
      okx: ['api_key', 'secret_key', 'passphrase'],
      hyperliquid: ['account_address', 'secret_key'],
      lighter: ['api_key_index', 'account_index', 'public_key', 'private_key'],
      // Extended: client_id optional (funds app may not store it; API can work without it)
      extended: ['api_key', 'stark_key_public', 'stark_key_private', 'vault_number'],
    };
    const keys = required[exchange];
    return keys && keys.every(k => status[k]);
  };

  const EXCHANGE_DISPLAY_NAMES = {
    binance: 'Binance',
    bybit: 'Bybit',
    okx: 'OKX',
    hyperliquid: 'Hyperliquid',
    lighter: 'Lighter.xyz',
    extended: 'Extended.exchange',
  };
  return (
    <div className="admin-settings">
      <div className="admin-header">
        <h2>API Keys Configuration</h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* API Keys: funds-style tiles – no input fields; Configured indicator + Test only */}
      <div className="api-keys-section">
        <h3 className="api-keys-section-title">Exchanges</h3>
        <p className="api-keys-hint">API keys are configured per account. Use Test to verify the connection.</p>
        {keyStatusSource === 'config' && selectedAccountId && (
          <div className="api-keys-db-hint" role="alert">
            Backend is using default config (cannot reach DB for this account). Start the Cloud SQL proxy (<code>./scripts/connect-cloud-sql-local.sh</code>) and restart the backend so credentials for this account load from the database.
          </div>
        )}
        <div className="exchange-tiles">
          {EXCHANGES.map((exchange) => {
            const configured = isExchangeConfigured(exchange);
            const testResult = testResults[exchange];
            const isTesting = testingExchange === exchange;
            return (
              <div
                key={exchange}
                className={`exchange-tile ${!configured ? 'not-configured' : ''}`}
              >
                <div className="tile-header">
                  <span className="tile-name">{EXCHANGE_DISPLAY_NAMES[exchange]}</span>
                </div>
                <div className="tile-status-row">
                  <div className="tile-status">
                    <span className={`status-dot ${configured ? 'configured' : 'not-configured'}`} />
                    <span className="status-text">{configured ? 'Configured' : 'Not configured'}</span>
                  </div>
                  <div className="test-btn-wrapper">
                    <span
                      className={`test-dot ${testResult ? (testResult.success ? 'success' : 'error') : 'default'}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleTest(exchange)}
                      className="btn-test-sm"
                      disabled={isTesting || !configured}
                    >
                      {isTesting ? '...' : 'Test'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Position Opening Times Section */}
      <div className="admin-section opening-times-section">
        <h3>Position Opening Times</h3>
        <p className="section-description">
          Set the actual trade opening date for each position. This is used to calculate 
          cumulative funding payments since the position was opened. If not set manually, 
          the system uses the time when the position was first detected.
        </p>
        
        {openingTimes.length === 0 ? (
          <p className="no-data">No positions tracked yet. Opening times will appear here once positions are detected.</p>
        ) : (
          <div className="opening-times-table">
            <table>
              <thead>
                <tr>
                  <th>Exchange</th>
                  <th>Pair</th>
                  <th>Opening Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {openingTimes.map((pos, idx) => (
                  <tr key={`${pos.exchange}-${pos.pair}-${idx}`}>
                    <td>{pos.exchange.toUpperCase()}</td>
                    <td>{pos.pair}</td>
                    <td>{formatDateTime(pos.opening_time)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditOpeningTime(pos)}
                          disabled={loading}
                        >
                          Edit
                        </button>
                        <button 
                          className="recalculate-btn"
                          onClick={() => handleRecalculateFunding(pos.exchange, pos.pair)}
                          disabled={loading || recalculatingPosition === `${pos.exchange}-${pos.pair}`}
                          title="Recalculate funding payments from stored rates (for Lighter) or API (for others)"
                        >
                          {recalculatingPosition === `${pos.exchange}-${pos.pair}` ? 'Recalculating...' : 'Recalculate Funding'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editingPosition && (
          <div className="edit-opening-time-modal">
            <h4>Edit Opening Time</h4>
            <div className="form-group">
              <label>
                {editingPosition.exchange.toUpperCase()} {editingPosition.pair}
              </label>
              <input
                type="datetime-local"
                value={editDateTime}
                onChange={(e) => setEditDateTime(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Note (optional)</label>
              <input
                type="text"
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="e.g., Manual entry - actual trade opening time"
              />
            </div>
            <div className="modal-actions">
              <button 
                className="save-btn"
                onClick={handleSaveOpeningTime}
                disabled={loading || !editDateTime}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button 
                className="cancel-btn"
                onClick={() => {
                  setEditingPosition(null);
                  setEditDateTime('');
                  setEditNote('');
                }}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminSettings;

